import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { WelcomeTrialParams } from 'reactApp/appHelpers/featuresHelpers/features.types';
import { hideOverquotaFeatures } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { quotaCleanerOnboardingEntryEnable } from 'reactApp/appHelpers/featuresHelpers/features/quotaCleaner';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useSendSpaceMenuAnalytics } from 'reactApp/hooks/UseSendSpaceMenuAnalytics';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { goToCleanerAndShowQuotaCleaner, loadUserQuotaCleanerList } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { widgetTitles } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.constants';
import { getFilteredLimitedGroups } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { QuotaCleanerGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import { useQuotaCleanerType } from 'reactApp/sections/QuotaLanding/hooks/quotaCleaner/useQuotaCleanerType';
import { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { useQuotaWarning } from 'reactApp/ui/Space/SingleSpace/hooks/useQuotaWarning';
import { type ClearItem, ClearBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/ClearBlock';
import { TrialBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/TrialBlock/TrialBlock';
import styles from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/WelcomeNewPromo.css';
import { useWelcomePromoLogic } from 'reactApp/ui/WelcomePromo/WelcomePromo.hooks';

interface Props {
    space?: string;
    trialData?: WelcomeTrialParams | null;
    items: ClearItem[];

    onSubscriptionsClick(): void;

    onClearClick(): void;
}

export const WelcomeOverquotaPromoComponent = memo(({ onSubscriptionsClick, onClearClick, items = [], trialData }: Props): ReactElement => {
    // tempexp_19379-start
    const { type: quotaCleanerType } = useQuotaCleanerType();
    const shouldQuotaCleanerShow = quotaCleanerType !== null;
    const isCloudOrMailOrApiOverQuotaFlag = useSelector(isCloudOrMailOrApiOverQuota);
    const hideCleaner = !(shouldQuotaCleanerShow && quotaCleanerOnboardingEntryEnable);
    const hideTrial = hideOverquotaFeatures?.trialOnboarding && isCloudOrMailOrApiOverQuotaFlag;
    // tempexp_19379-end

    const header = useQuotaWarning() || 'Пространство почти заполнено';

    const description = useMemo(() => {
        return (
            <>
                <span className={styles.link} onClick={onClearClick}>
                    Освободите место
                </span>
                , удалив файлы из&nbsp;Облака или вложения
                <br />
                из&nbsp;Почты. Или добавьте&nbsp;гигабайты
                <span className={styles.link} onClick={onSubscriptionsClick}>
                    {' '}
                    с подпиской Mail Space
                </span>
            </>
        );
    }, []);

    return (
        <div className={styles.root}>
            <div className={styles.textBlock}>
                <div className={styles.header}>{header}</div>
                <div className={styles.description}>{description}</div>
            </div>
            {(!hideCleaner || !hideTrial) && (
                <div className={styles.actionBlock}>
                    <div className={styles.wrapper}>
                        {!hideCleaner && <ClearBlock items={items} onClearClick={onClearClick} />}
                        {!hideTrial && trialData && <TrialBlock data={trialData} forQuota />}
                    </div>
                </div>
            )}
        </div>
    );
});

WelcomeOverquotaPromoComponent.displayName = 'WelcomeOverquotaPromoComponent';

const MAX_CLEAR_ITEMS = 4;

const neededGroupsIds = [
    UserQuotaGroupId.Read,
    UserQuotaGroupId.LargeInbox,
    UserQuotaGroupId.LargeSent,
    UserQuotaGroupId.OldInbox,
    UserQuotaGroupId.MailBin,
    UserQuotaGroupId.MailSpam,
    UserQuotaGroupId.Newsletters,
    UserQuotaGroupId.OldSent,
    UserQuotaGroupId.WithAttaches,
];

export const WelcomeOverquotaPromo = memo((): ReactElement => {
    const dispatch = useDispatch();
    const { trialData, handleSubscriptionsClick } = useWelcomePromoLogic({ forNewUser: false, isOverquota: true });
    const groups = useSelector(getFilteredLimitedGroups);
    const sendAnalytics = useSendSpaceMenuAnalytics(false);

    const onClearItemClick = useCallback(
        (group: QuotaCleanerGroup) => {
            dispatch(
                goToCleanerAndShowQuotaCleaner({
                    groupId: group.groupId,
                    size: group.size,
                    from: 'welcomeOverquotaGroup',
                })
            );
        },
        [dispatch]
    );

    const items = useMemo(
        () =>
            groups
                .filter((group) => neededGroupsIds.includes(group.groupId))
                .filter((_, i) => i < MAX_CLEAR_ITEMS)
                .map((group) => ({
                    size: bytesToNDigits(group.size, 3).value,
                    title: widgetTitles[group.groupId],
                    onClick: () => onClearItemClick(group),
                })),
        [groups, onClearItemClick]
    );

    const handleClearClick = useCallback(() => {
        sendAnalytics('clear');
        dispatch(
            historyPush({
                id: getIdByStorage(EStorageType.quotaCleaner),
                search: `?${PaymentUTM.welcomeOverquota}`,
                hash: 'cleaner',
            })
        );
    }, []);

    useEffect(() => {
        dispatch(loadUserQuotaCleanerList());
    }, [dispatch]);

    return (
        <WelcomeOverquotaPromoComponent
            onSubscriptionsClick={handleSubscriptionsClick}
            onClearClick={handleClearClick}
            trialData={trialData}
            items={items}
        />
    );
});

WelcomeOverquotaPromo.displayName = 'WelcomeOverquotaPromo';
